import type { IModularBenefitDetail } from '@ben/shared/types/columbia';
import { findIndex, get } from 'lodash';
import { useCallback, useMemo, useRef } from 'react';
import type { FieldPath } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  deduplicateRestrictions,
  ensureComponentExists,
  ensureStepExists,
  ensureSubComponentExists,
  type ComponentData,
} from './life-event-restriction-form.utils';

interface UseLifeEventRestrictionFormParams {
  lifeEventIndex: number;
  stepId: number;
  component: ComponentData;
  parentComponent?: ComponentData;
  autoRegister?: boolean;
}

interface UseLifeEventRestrictionFormResult {
  restrictionsBasePath: string;
  restrictionSetPath: string;
  restrictionAppliedPath: FieldPath<IModularBenefitDetail>;
  componentPath: string;
  stepArrayIndex: number;
  componentArrayIndex: number;
  subComponentArrayIndex: number;
  componentExistsInForm: boolean;
  upsertComponent: () => void;
  updateRestrictionApplied: (value: boolean) => void;
}

/**
 * Hook that manages life event restriction form state.
 * Handles deduplication, finding/creating step and component entries.
 * Sub-components store their restrictions within the parent's subComponents array.
 *
 * @param autoRegister - If true, automatically registers the component in the form
 *                       when the hook is first called. This ensures all displayed
 *                       components appear in the payload.
 */
const useLifeEventRestrictionForm = ({
  lifeEventIndex,
  stepId,
  component,
  parentComponent,
  autoRegister = false,
}: UseLifeEventRestrictionFormParams): UseLifeEventRestrictionFormResult => {
  const { setValue, getValues } = useFormContext<IModularBenefitDetail>();
  const hasAutoRegistered = useRef(false);

  const isSubComponent = parentComponent !== undefined;

  const restrictionsBasePath =
    `lifeEventPolicies.${lifeEventIndex}.restrictions` as const;

  const lifeEventRestrictions = useWatch({
    name: restrictionsBasePath,
    defaultValue: [],
  });

  const {
    stepArrayIndex,
    componentArrayIndex,
    subComponentArrayIndex,
    componentExistsInForm,
  } = useMemo(() => {
    const cleanedRestrictions = deduplicateRestrictions(
      lifeEventRestrictions ?? []
    );

    const existingStepIndex = findIndex(
      cleanedRestrictions,
      (restriction) => restriction.stepId === stepId
    );
    const stepExists = existingStepIndex >= 0;
    const foundStepIndex = stepExists
      ? existingStepIndex
      : cleanedRestrictions.length;

    if (isSubComponent && parentComponent) {
      let foundParentIndex = 0;
      let foundSubComponentIndex = 0;
      let componentExists = false;

      if (stepExists) {
        const currentStep = cleanedRestrictions[existingStepIndex];
        const parentComponentIndex = findIndex(
          currentStep.components,
          (currentComponent) =>
            currentComponent.componentId === parentComponent.componentId
        );

        foundParentIndex =
          parentComponentIndex >= 0
            ? parentComponentIndex
            : currentStep.components.length;

        if (parentComponentIndex >= 0) {
          const parentComponentData =
            currentStep.components[parentComponentIndex];
          const subComponentsList = get(
            parentComponentData,
            'subComponents',
            []
          );

          const existingSubComponentIndex = findIndex(
            subComponentsList,
            (subComponent) => subComponent.componentId === component.componentId
          );

          foundSubComponentIndex =
            existingSubComponentIndex >= 0
              ? existingSubComponentIndex
              : subComponentsList.length;

          componentExists = existingSubComponentIndex >= 0;
        }
      }

      return {
        stepArrayIndex: foundStepIndex,
        componentArrayIndex: foundParentIndex,
        subComponentArrayIndex: foundSubComponentIndex,
        componentExistsInForm: componentExists,
      };
    }

    let foundComponentIndex = 0;
    let componentExists = false;

    if (stepExists) {
      const currentStep = cleanedRestrictions[existingStepIndex];
      const existingComponentIndex = findIndex(
        currentStep.components,
        (currentComponent) =>
          currentComponent.componentId === component.componentId
      );

      foundComponentIndex =
        existingComponentIndex >= 0
          ? existingComponentIndex
          : currentStep.components.length;

      componentExists = existingComponentIndex >= 0;
    }

    return {
      stepArrayIndex: foundStepIndex,
      componentArrayIndex: foundComponentIndex,
      subComponentArrayIndex: -1,
      componentExistsInForm: componentExists,
    };
  }, [
    lifeEventRestrictions,
    stepId,
    component.componentId,
    parentComponent,
    isSubComponent,
  ]);

  const componentPath = isSubComponent
    ? (`lifeEventPolicies.${lifeEventIndex}.restrictions.${stepArrayIndex}.components.${componentArrayIndex}.subComponents.${subComponentArrayIndex}` as const)
    : (`lifeEventPolicies.${lifeEventIndex}.restrictions.${stepArrayIndex}.components.${componentArrayIndex}` as const);

  const restrictionSetPath = `${componentPath}.restrictionSet` as const;

  const upsertComponent = useCallback(() => {
    const currentRestrictions = getValues(restrictionsBasePath) ?? [];
    const cleaned = deduplicateRestrictions(currentRestrictions);

    const targetStepIndex = ensureStepExists(cleaned, stepId);

    if (isSubComponent && parentComponent) {
      const parentIndex = ensureComponentExists(
        cleaned,
        targetStepIndex,
        parentComponent,
        true
      );
      ensureSubComponentExists(
        cleaned,
        targetStepIndex,
        parentIndex,
        component
      );
    } else {
      ensureComponentExists(cleaned, targetStepIndex, component);
    }

    setValue(restrictionsBasePath, cleaned, { shouldDirty: true });
  }, [
    getValues,
    setValue,
    restrictionsBasePath,
    stepId,
    component,
    parentComponent,
    isSubComponent,
  ]);

  const restrictionAppliedPath =
    `${componentPath}.restrictionApplied` as FieldPath<IModularBenefitDetail>;

  const updateRestrictionApplied = useCallback(
    (value: boolean) => {
      upsertComponent();

      setValue(restrictionAppliedPath, value as never, {
        shouldDirty: true,
      });
    },
    [upsertComponent, setValue, restrictionAppliedPath]
  );

  if (autoRegister && !componentExistsInForm && !hasAutoRegistered.current) {
    hasAutoRegistered.current = true;
    // Defer the upsert to avoid updating state during render
    queueMicrotask(() => {
      upsertComponent();
    });
  }

  return {
    restrictionsBasePath,
    restrictionSetPath,
    restrictionAppliedPath,
    componentPath,
    stepArrayIndex,
    componentArrayIndex,
    subComponentArrayIndex,
    componentExistsInForm,
    upsertComponent,
    updateRestrictionApplied,
  };
};

export default useLifeEventRestrictionForm;
